import { Box, Container, IconButton, Typography } from '@mui/material';
import { MunicipalTabulation, ProvincialDistrictTabulation, Tabulation } from '../../types';
import React, { useEffect, useState } from 'react';
import { Unsubscribe, collection, doc, getDocs, onSnapshot, updateDoc } from 'firebase/firestore';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Html5QrcodeScanner } from "html5-qrcode";
import { QRCodeSVG } from 'qrcode.react';
import TabulationDash from './TabulationDash';
import { db } from '../../firebase';
import useAuth from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface QRAuthCodeProps {
  dashboardId: string;
  onSuccess: () => void;
}

const QRAuthCode: React.FC<QRAuthCodeProps> = ({ dashboardId, onSuccess }) => {
  const [randomCode, setRandomCode] = useState<string>('');

  const generateRandomCode = () => {
    // Generate a random 6-digit code
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setRandomCode(code);
  };

  useEffect(() => {
    // Generate initial code
    generateRandomCode();

    // Optional: Regenerate code every 5 minutes
    const intervalId = setInterval(generateRandomCode, 5 * 60 * 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "tabulationsV2", dashboardId), (doc) => {
      const data = doc.data();
      console.log("Current data: ", data);
      if (data?.tvCode === randomCode) {
        console.log("Code matched!");
        onSuccess();
      } else {
        console.log(`Code not matched. Expected: ${randomCode}, Actual: ${data?.tvCode}`);
      }
    });

    return () => unsub();
  }, [randomCode]);

  return (
    <Container 
      maxWidth="sm" 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh' 
      }}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          p: 3, 
          border: '2px solid', 
          borderColor: 'primary.main', 
          borderRadius: 2 
        }}
      >
        <Typography variant="h4" gutterBottom>
          Survey Polls
        </Typography>
        
        <QRCodeSVG 
          value={randomCode} 
          size={256} 
          level={'H'} 
        />
        
        <Typography variant="h5" sx={{ mt: 2 }}>
          Code: {randomCode}
        </Typography>
      </Box>
    </Container>
  );
};

interface QRAuthScannerProps {
  dashboardId: string;
  onNavigate: () => void;
}

const QRAuthScanner: React.FC<QRAuthScannerProps> = ({ dashboardId, onNavigate }) => {
  const html5QrcodeScanner = React.useRef<Html5QrcodeScanner | null>(null);

  const onScanSuccess = async (decodedText: string, decodedResult: object) => {
    // handle the scanned code as you like, for example:
    console.log(`Code matched = ${decodedText}`, decodedResult);
    
    // Stop scanning
    html5QrcodeScanner.current?.clear();
    
    const tab = doc(db, "tabulationsV2", dashboardId);
    await updateDoc(tab, { tvCode: decodedText });
    
    // Navigate after successful scan
    onNavigate();
  }
  
  const onScanFailure = (error: string) => {
    // handle scan failure, usually better to ignore and keep scanning.
    // for example:
    console.warn(`Code scan error = ${error}`);
  }

  useEffect(() => {
    html5QrcodeScanner.current = new Html5QrcodeScanner(
      "reader",
      { fps: 10, qrbox: {width: 250, height: 250} },
      /* verbose= */ false);
    html5QrcodeScanner.current?.render(onScanSuccess, onScanFailure);
  }, []);

  return (
    <Container 
      maxWidth="sm" 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh' 
      }}
    >
      <div id="reader"></div>
    </Container>
  );
};

const TVMonitor: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [districtTabs, setDistrictTabs] = useState<ProvincialDistrictTabulation[]>([]);
  const [tabulations, setTabulations] = useState<Tabulation[]>([]);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [isQRAuthenticated, setIsQRAuthenticated] = useState(false);
  const [showQRAuthScanner, setShowQRAuthScanner] = useState(false);
  const [generateQRAuthCode, setGenerateQRAuthCode] = useState(false);

  const tabDistrictsColPath = 'tabulationsV2/SURIGAO DEL NORTE_DISTRICT 1/regions/REGION XIII/provinces/SURIGAO DEL NORTE/districts';

  const getTabulations = async (tabDistrictsColPath: string): Promise<Tabulation[]> => {
    const districtsCol = collection(db, tabDistrictsColPath);
    const districsSnap = await getDocs(districtsCol);
    const districtsTabs = districsSnap.docs.map((d) => d.data() as ProvincialDistrictTabulation);
    setDistrictTabs(districtsTabs);

    const cityTabs: Tabulation[] = [];
    for (const districtTab of districtsTabs) {
      const citySnap = await getDocs(collection(districtsCol, `${districtTab.district}/cities`));
      cityTabs.push(...citySnap.docs.map((d) => d.data() as MunicipalTabulation));
    }

    const allTabulations = [
      ...districtsTabs,
      ...cityTabs
    ];

    return allTabulations;
  };
  
  useEffect(() => {
    if (!loading) {
      if (user) {
        setShowQRAuthScanner(true);
      } else if (isQRAuthenticated) {
        getTabulations(tabDistrictsColPath).then(setTabulations);
      } else {
        setGenerateQRAuthCode(true);
      }
    }
  }, [user, loading, isQRAuthenticated]);

  useEffect(() => {
    if (districtTabs.length > 0) {
      const subs: Unsubscribe[] = [];
      for (const tab of districtTabs) {
        subs.push(
          onSnapshot(
            doc(db, tabDistrictsColPath, tab.id), 
            (snapshot) => {
              console.log(snapshot.data());
              getTabulations(tabDistrictsColPath).then(setTabulations);
            }
          )
        );
      }
      return () => {
        for (const sub of subs) {
          sub();
        }
      }
    }
  }, [districtTabs.length]);

  const handleTabComplete = () => {
    if (tabulations.length > 0) {
      setCurrentTabIndex((prevIndex) => (prevIndex + 1) % tabulations.length);
    }
  };

  const navigateTab = (direction: 'next' | 'prev') => {
    const totalTabs = tabulations.length;
    setCurrentTabIndex((prevIndex) => {
      if (direction === 'next') {
        return (prevIndex + 1) % totalTabs;
      } else {
        return (prevIndex - 1 + totalTabs) % totalTabs;
      }
    });
  };
  
  // If loading, return null to prevent flash of content
  if (loading) {
    return null;
  }

  if (generateQRAuthCode) {
    return (
      <QRAuthCode 
        dashboardId='SURIGAO DEL NORTE_DISTRICT 1'
        onSuccess={() => {
          setIsQRAuthenticated(true);
          setShowQRAuthScanner(false);
          setGenerateQRAuthCode(false);
        }}
      />
    );
  }

  if (showQRAuthScanner) {
    return (
      <QRAuthScanner 
        dashboardId='SURIGAO DEL NORTE_DISTRICT 1'
        onNavigate={() => navigate('/dashboard')}
      />
    );
  }

  return (
    <Box position="relative" height="100%" overflow="hidden">
      {tabulations.length > 0 && (
        <Box 
          sx={{
            transition: 'opacity 0.5s ease-in-out',
            opacity: 1,
            position: 'relative',
            display: 'flex',
            alignItems: 'start'
          }}
        >
          {tabulations.length > 1 && (
            <IconButton 
              onClick={() => navigateTab('prev')} 
              sx={{ 
                position: 'absolute', 
                left: 0, 
                zIndex: 10 
              }}
            >
              <ArrowBackIosIcon />
            </IconButton>
          )}

          <TabulationDash 
            tabulation={tabulations[currentTabIndex]} 
            navToStats={false} 
            showAll={false} 
            onComplete={handleTabComplete}
          />
          
          {tabulations.length > 1 && (
            <IconButton 
              onClick={() => navigateTab('next')} 
              sx={{ 
                position: 'absolute', 
                right: 0, 
                zIndex: 10 
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          )}
        </Box>
      )}
      {tabulations.length > 1 && (
        <Box 
          display="flex" 
          justifyContent="center" 
          mt={2}
        >
          {tabulations.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: index === currentTabIndex ? 'primary.main' : 'grey.300',
                margin: '0 5px',
                cursor: 'pointer'
              }}
              onClick={() => setCurrentTabIndex(index)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TVMonitor;
